import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { Button, Icon, TextCaption } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { DragEvent as ReactDragEvent, FC, useRef, useState } from 'react';

import { ConfigPanel } from '../components/table/src/config-panel';
import { useProStudioViewerRole } from '../hooks';
import { tableStore } from '../stores/table-store';
import { isGroupRow } from '../stores/utils/group-row-guards';
import { isServiceScriptFile, transformActionFileData } from '../utils';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { systemActionNodeItems } from '../components/table/resource-lib/system-action-nodes';
import { ActionsContent } from './actions';
import { useTopUnderBuildBar } from './build-panel-position';

import './build-actions-panel.scss';

/**
 * Build's Actions panel (prototype `prostudio-build-flow-layouts.html`,
 * `renderActionsPanel`, from the Sep 29 status check): the commands this
 * solution's action nodes run — its own (Custom) and the platform's built-in
 * ones (System) — each with how many nodes use it and in which agents; then
 * the solution's scripts and config files, which the old standalone Actions
 * page held.
 */

interface CommandUse {
  command: string;
  nodes: number;
  agents: string[];
}

/** Every command an action node runs, with its nodes and agents. */
const commandsInUse = (): CommandUse[] => {
  const rows = tableStore.getAllSortedRows();
  const groupName = new Map<string, string>();
  rows.forEach((r) => {
    if (isGroupRow(r)) {
      groupName.set(r.id, String(r.settings?.groupName ?? '').trim());
    }
  });
  const uses = new Map<string, { nodes: number; agents: Set<string> }>();
  rows.forEach((r) => {
    if (r.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.A) return;
    const command = String(r.command ?? '').trim();
    if (!command) return;
    const use = uses.get(command) ?? { nodes: 0, agents: new Set<string>() };
    use.nodes += 1;
    const agent = groupName.get(String(r.settings?.groupId ?? ''));
    if (agent) use.agents.add(agent);
    uses.set(command, use);
  });
  return [...uses.entries()]
    .map(([command, u]) => ({
      command,
      nodes: u.nodes,
      agents: [...u.agents].sort(),
    }))
    .sort((a, b) => a.command.localeCompare(b.command));
};

/** "2 nodes · Knowledge Agent, Supervisor Agent" */
const usageText = (use: CommandUse) =>
  `${use.nodes} node${use.nodes === 1 ? '' : 's'}${
    use.agents.length
      ? ` · ${use.agents.slice(0, 2).join(', ')}${
          use.agents.length > 2 ? ` +${use.agents.length - 2}` : ''
        }`
      : ''
  }`;

/**
 * Hand a command to the canvas's (and Table View's) Resource Library drop:
 * one Action node running it, the same payload a Library card carries.
 */
const startCommandDrag = (command: string, e: DragEvent | ReactDragEvent) => {
  const item = systemActionNodeItems([command])[0];
  if (!item?.data || !e.dataTransfer) return;
  e.dataTransfer.effectAllowed = 'copy';
  e.dataTransfer.setData('text/plain', command);
  tableStore.setResourceLibDragData({ data: item.data, name: command });
};
const endCommandDrag = () => tableStore.setResourceLibDragData(null);
/** A script row's drag — module-level, so the file table's columns stay put. */
const startScriptDrag = (fileName: string, e: DragEvent) =>
  startCommandDrag(fileName.replace(/\.py$/, ''), e);

const CommandRow: FC<{ use: CommandUse; draggable: boolean }> = ({
  use,
  draggable,
}) => (
  <li
    className="c-build-actions__row"
    draggable={draggable}
    title={draggable ? 'Drag onto the canvas to add an action node' : undefined}
    onDragStart={
      draggable ? (e) => startCommandDrag(use.command, e) : undefined
    }
    onDragEnd={draggable ? endCommandDrag : undefined}
  >
    {draggable && (
      <Icon
        name="drag-indicator"
        size="xsmall"
        className="c-build-actions__grip"
      />
    )}
    <Icon
      name="layout-code"
      size="small"
      className="c-build-actions__code-icon"
    />
    <span className="c-build-actions__body">
      <span className="c-build-actions__name">{use.command}</span>
      <span className="c-build-actions__meta">{usageText(use)}</span>
    </span>
  </li>
);

/** Missing scripts named before "and N more". */
const MISSING_SHOWN = 5;

export const BuildActionsPanel: FC = observer(() => {
  const isViewer = useProStudioViewerRole();
  const top = useTopUnderBuildBar();
  // The Custom section's file input, which uploads to the Agent Manager.
  const upload = useRef<HTMLInputElement>(null);
  const scripts = useProStudioCtxSelector(
    (ctx) =>
      (ctx.draft?.files ?? [])
        .filter(isServiceScriptFile)
        .map((f) => transformActionFileData(f).name),
    { isEqualityDeep: true }
  );

  const [query, setQuery] = useState('');
  const [allMissing, setAllMissing] = useState(false);
  const needle = query.trim().toLowerCase();

  // Recomputed as rows change: the store is observable.
  const uses = commandsInUse();
  const system = new Set(tableStore.actionNodeNames);
  const builtIn = uses.filter(
    (u) =>
      system.has(u.command) &&
      (!needle || u.command.toLowerCase().includes(needle))
  );
  const uploaded = new Set(scripts);
  const byCommand = new Map(uses.map((u) => [u.command, u]));
  // A custom command a node runs, with no script to run.
  const missing = uses.filter(
    (u) => !system.has(u.command) && !uploaded.has(`${u.command}.py`)
  );

  /**
   * Each custom command is listed once — as its script, in the Custom
   * section, with where the solution uses it — rather than as a command and
   * again as a file.
   */
  const scriptNote = (fileName: string) => {
    if (!fileName.endsWith('.py')) return 'Supporting file';
    const use = byCommand.get(fileName.replace(/\.py$/, ''));
    return use ? usageText(use) : 'Not used by any group yet';
  };

  return (
    <div
      className="c-build-actions"
      style={{ top }}
      data-testid="bobcat-build-actions-panel"
    >
      <ConfigPanel
        title="Actions"
        extra={
          !isViewer && (
            <Button
              size="small"
              test-id="bobcat-build-actions-new"
              onClick={() => upload.current?.click()}
            >
              <Icon slot="prefix" name="add" size="xsmall" />
              New action
            </Button>
          )
        }
        onClose={() => tableStore.setActionsPanelOpen(false)}
      >
        <TextCaption variant="secondary">
          The commands this solution’s action nodes run. Drag one onto the
          canvas to add a node that runs it.
        </TextCaption>

        {missing.length > 0 && (
          <div className="c-build-actions__missing">
            <strong>
              {missing.length === 1
                ? 'A node runs a command with no script:'
                : `${missing.length} commands have no script:`}
            </strong>{' '}
            {/* A big import can be missing dozens; the first few say enough. */}
            {(allMissing ? missing : missing.slice(0, MISSING_SHOWN))
              .map((u) => `${u.command}.py`)
              .join(', ')}
            {missing.length > MISSING_SHOWN && !allMissing ? (
              <>
                {' '}
                and {missing.length - MISSING_SHOWN} more.{' '}
                <button
                  type="button"
                  className="c-build-actions__missing-more"
                  onClick={() => setAllMissing(true)}
                >
                  Show all
                </button>
              </>
            ) : (
              '.'
            )}{' '}
            Upload with New action.
          </div>
        )}
        <input
          type="search"
          className="c-build-actions__search"
          placeholder="Search actions…"
          aria-label="Search actions"
          data-testid="bobcat-build-actions-search"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        <div className="c-build-actions__files">
          <ActionsContent
            scriptsUploadRef={upload}
            query={query}
            scriptNote={scriptNote}
            onScriptDragStart={startScriptDrag}
            onScriptDragEnd={endCommandDrag}
            betweenSections={
              builtIn.length > 0 && (
                <div className="c-build-actions__system">
                  <span className="c-build-actions__group">
                    System · {builtIn.length}
                  </span>
                  <ul className="c-build-actions__list">
                    {builtIn.map((u) => (
                      <CommandRow
                        key={u.command}
                        use={u}
                        draggable={!isViewer}
                      />
                    ))}
                  </ul>
                </div>
              )
            }
          />
        </div>
      </ConfigPanel>
    </div>
  );
});
